import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { DataProvider } from "@/lib/data";

const AuthContext = createContext(null);

// Staff roles: home screen + allowed path prefixes
export const STAFF_PATHS = {
  cutter: { home: "/kesim", allow: ["/kesim", "/kesim-fis/"] },
  shipper: { home: "/sevkiyat", allow: ["/sevkiyat", "/irsaliye/"] },
};
export const isAdmin = (u) => u?.role === "admin";
export const useAuth = () => useContext(AuthContext);

export const formatApiError = (detail) => {
  if (detail == null) return "Bir hata oluştu, tekrar deneyin.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail.msg || String(detail);
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const loggedIn = useRef(false);
  useEffect(() => {
    // Restore session: try access cookie, then refresh cookie; ignore if user logged in meanwhile
    api.get("/auth/me").catch(() => api.post("/auth/refresh"))
      .then((r) => { if (!loggedIn.current) setUser(r.data); })
      .catch(() => { if (!loggedIn.current) setUser(false); });
  }, []);
  useEffect(() => {
    const onExpired = () => setUser(false);
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, []);
  const login = useCallback(async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    loggedIn.current = true;
    setUser(r.data);
  }, []);
  const logout = useCallback(async () => { await api.post("/auth/logout").catch(() => {}); loggedIn.current = false; setUser(false); }, []);
  return <AuthContext.Provider value={{ user, login, logout }}>{children}</AuthContext.Provider>;
}

export function RequireAuth() {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-[#8C7B68]" data-testid="auth-checking">Yükleniyor...</div>;
  if (!user) return <Navigate to="/giris" replace state={{ from: location.pathname }} />;
  const staff = STAFF_PATHS[user.role];
  if (staff && !staff.allow.some((p) => location.pathname === p || (p.endsWith("/") && location.pathname.startsWith(p))))
    return <Navigate to={staff.home} replace />;
  return <DataProvider role={user.role}><Outlet /></DataProvider>;
}
